"use client";
// status: updated
import React, { useEffect, useMemo, useRef } from "react";
// PERF: GSAP is loaded lazily (dynamic import) inside the effect so it stays out
// of the initial bundle. This is a pure gsap.to animation (no ScrollTrigger), so
// we import "gsap" directly rather than the ScrollTrigger helper.
import type GsapNS from "gsap";
import { Images } from "@assets/assets";

interface ValuesOrbitProps {
  className?: string;
}

export default function ValuesOrbit({ className = "" }: ValuesOrbitProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  // Keep render-time geometry consistent with animation-time geometry
  const size = 600;
  const cx = size / 2;
  const cy = size / 2;
  const margin = 8;
  const maxR = cx - margin;
  const ringWidthForPrevLayout = 28;
  const stepOthers = (maxR - ringWidthForPrevLayout / 2) / 10;
  // Keep 6,8,10 same as before; make the smallest ring reduced further
  const smallestScale = 0.9; // reduce first ring radius more than before
  const r1 = 4.5 * stepOthers * smallestScale;
  const r2 = 6 * stepOthers;
  const r3 = 8 * stepOthers;
  const r4 = 10 * stepOthers;
  const radii = useMemo(() => [r1, r2, r3, r4], [r1, r2, r3, r4]);
  const innerRadius = r1;

  // Assume all circles touch the same bottom baseline. Compute individual centers.
  const baselineY = cy + r4; // bottom of the largest circle
  const centersY = useMemo(
    () => [baselineY - r1, baselineY - r2, baselineY - r3, baselineY - r4],
    [baselineY, r1, r2, r3, r4],
  );
  const innerCenterY = baselineY - innerRadius;

  const ringLabels = [
    { idx: 3, title: "Flexibility", subtitle: "Work your way" },
    { idx: 2, title: "Wellbeing", subtitle: "Health first always" },
    { idx: 1, title: "Growth", subtitle: "Learn and evolve" },
  ];

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;

    const dots = Array.from(
      svg.querySelectorAll<SVGCircleElement>(".orbit-dot"),
    );
    if (dots.length === 0) return;

    // Coordinate system based on the same viewBox used below
    const sizeLocal = 600;
    const cxLocal = sizeLocal / 2;
    const centersYLocal = centersY;
    const radiiLocal = radii;

    // Start angles for separation
    const startAngles = [
      Math.PI * -0.5,
      Math.PI / 3,
      Math.PI * 1.1,
      Math.PI * 1.7,
    ];
    // Larger radius should be slower -> duration grows with radius
    const baseDuration = 32;

    const anims: ReturnType<(typeof GsapNS)["to"]>[] = [];
    let cancelled = false;

    // Position dots immediately (no GSAP needed) to avoid any visual pop-in,
    // then attach the looping tweens once GSAP has loaded.
    const positioners = dots.map((dot, i) => {
      const angleObj = { a: startAngles[i] || 0 };
      const r = radiiLocal[i];
      const updatePosition = () => {
        const x = cxLocal + r * Math.cos(angleObj.a);
        const y = centersYLocal[i] + r * Math.sin(angleObj.a);
        dot.setAttribute("cx", x.toString());
        dot.setAttribute("cy", y.toString());
      };
      updatePosition();
      return { angleObj, updatePosition, i };
    });

    import("gsap").then(({ gsap }) => {
      if (cancelled) return;
      positioners.forEach(({ angleObj, updatePosition, i }) => {
        const duration = baseDuration + i * 4;
        const t = gsap.to(angleObj, {
          a: angleObj.a + Math.PI * 2,
          duration,
          ease: "none",
          repeat: -1,
          onUpdate: updatePosition,
        });
        anims.push(t);
      });
    });

    return () => {
      cancelled = true;
      anims.forEach((a) => a.kill());
    };
  }, [centersY, radii]);

  return (
    <div className={`w-[100%] lg:w-[80%] mx-auto ${className}`}>
      <svg
        ref={svgRef}
        viewBox="0 0 600 600"
        width="100%"
        preserveAspectRatio="xMidYMid meet"
        style={{ height: "auto", aspectRatio: "1 / 1" }}
      >
        <defs>
          {/* Soft shadow for orbiting dots */}
          <filter id="dotShadow" x="-50%" y="-50%" width="200%" height="200%">
            <feDropShadow
              dx="0"
              dy="2"
              stdDeviation="2"
              floodColor="#000"
              floodOpacity="0.2"
            />
          </filter>
          {/* Vertical blue gradient used by outer three rings */}
          <linearGradient id="ringGrad" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#225BD4" stopOpacity="0.20" />
            <stop offset="100%" stopColor="#225BD4" stopOpacity="0.02" />
          </linearGradient>
        </defs>

        {/* Filled concentric backgrounds (gradient) aligned to baseline */}
        <circle
          cx={300}
          cy={centersY[3]}
          strokeWidth="1"
          r={radii[3]}
          fill="url(#ringGrad)"
        />
        <circle
          cx={300}
          cy={centersY[2]}
          strokeWidth="1"
          r={radii[2]}
          fill="url(#ringGrad)"
        />
        <circle
          cx={300}
          cy={centersY[1]}
          strokeWidth="1"
          r={radii[1]}
          fill="url(#ringGrad)"
        />

        {/* Inner solid circle base */}
        <circle cx={300} cy={innerCenterY} r={innerRadius} fill="#ffffff" />

        {/* Labels on the three large rings */}
        {ringLabels?.map(({ idx, title, subtitle }) => {
          const cyRing = centersY[idx];
          const rRing = radii[idx];
          const titleY = cyRing - rRing + 60;
          const subY = titleY + 22;
          return (
            <g key={idx}>
              <text
                x={300}
                y={titleY}
                textAnchor="middle"
                className="clash-font"
                fontSize={24}
                fontWeight={500}
                fill="#00040B"
              >
                {title}
              </text>
              <text
                x={300}
                y={subY}
                textAnchor="middle"
                fontSize={13}
                fontWeight={500}
                fill="#00040B"
                opacity={0.8}
              >
                {subtitle}
              </text>
            </g>
          );
        })}

        {/* Center logo */}
        <image
          href={Images?.logoIcon}
          x={300 - 45}
          y={innerCenterY - 45}
          width={90}
          height={90}
          preserveAspectRatio="xMidYMid meet"
        />

        {/* Orbiting dots */}
        <circle
          className="orbit-dot"
          r="7"
          filter="url(#dotShadow)"
          fill="#225BD4"
          stroke="#ffffff"
          strokeWidth="1"
        />
        <circle
          className="orbit-dot"
          r="7"
          filter="url(#dotShadow)"
          fill="#225BD4"
          stroke="#ffffff"
          strokeWidth="1"
        />
        <circle
          className="orbit-dot"
          r="7"
          filter="url(#dotShadow)"
          fill="#225BD4"
          stroke="#ffffff"
          strokeWidth="1"
        />
        <circle
          className="orbit-dot"
          r="7"
          filter="url(#dotShadow)"
          fill="#225BD4"
          stroke="#ffffff"
          strokeWidth="1"
        />
      </svg>
    </div>
  );
}
